import { ClassifiersService } from '@amdb/drone/@core/services';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Classifier } from '../../models';
import { TextType } from '../../models/text-type.model';

@Component({
    selector: 'amdb-text-type-input',
    templateUrl: './text-type-input.component.html',
    styleUrls: ['./text-type-input.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class TextTypeInputComponent implements OnInit {
    @Input() textTypeEntries: TextType[] = [];
    @Output() textTypeEntriesChange: EventEmitter<TextType[]> = new EventEmitter<TextType[]>();

    @Input() controlName: string;
    @Input() controlLabel: string;

    @Input() longText: boolean = false;
    @Input() required: boolean = false;

    @Input() requiredLanguageIds: number[] = [];

    languages: Classifier[];

    get canAddNewEntries(): boolean {
        if (!this.languages || !this.textTypeEntries) {
            return false;
        }

        return this.textTypeEntries.length < this.languages.length;
    }

    constructor(private classifierService: ClassifiersService) {}

    async ngOnInit(): Promise<void> {
        this.languages = await this.classifierService.getLangClassifiers();

        if (!this.textTypeEntries) {
            this.textTypeEntries = [];
        }

        if (!this.required && this.textTypeEntries.length == 0) {
            return;
        }

        this.checkRequiredLanguagesUsed();
    }

    addText(): void {
        if (!this.required && this.textTypeEntries.length == 0 && this.requiredLanguageIds.length > 0) {
            // case when field is not required and empty, on first add
            // we need to populate all required languages for this field
            this.requiredLanguageIds.forEach((languageId: number): void => {
                this.addTextTypeEntry(languageId);
            });
            return;
        }

        this.addTextTypeEntry();
    }

    removeText(index: number): void {
        this.textTypeEntries.splice(index, 1);
        if (this.textTypeEntries.length === 0 && this.required) {
            this.addTextTypeEntry();
            return;
        }

        this.entriesChanged();
    }

    clearAll(): void {
        this.textTypeEntries = [];
        if (this.required && this.requiredLanguageIds.length > 0) {
            this.checkRequiredLanguagesUsed();
        }
        this.entriesChanged();
    }

    checkRequiredLanguagesUsed(): void {
        if (this.requiredLanguageIds.length > 0) {
            this.requiredLanguageIds.forEach((languageId: number): void => {
                const textTypeWithLanguage: TextType = this.textTypeEntries.find((textType: TextType): boolean => textType.languageId == languageId);
                if (textTypeWithLanguage) {
                    return;
                }

                this.addTextTypeEntry(languageId);
            });
        }
    }

    getLanguagesForEntry(index: number): Classifier[] {
        const usedLanguageIds = new Set<number>(
            this.textTypeEntries
                .filter((text: TextType, i: number): boolean => i !== index) // filter out current one
                .map((x: TextType): number => x.languageId) // get only language ids
                .filter((id: number): boolean => id != null), // exclude the empty ones
        );

        const currentLanguageId: number = this.textTypeEntries[index]?.languageId;

        return (this.languages || []).filter((lang: Classifier): boolean => {
            // keep currently selected value visible for this row
            if (currentLanguageId != null && currentLanguageId == lang.id) {
                return true;
            }

            return !usedLanguageIds.has(lang.id);
        });
    }

    entryHasRequiredLanguage(textTypeEntry: TextType): boolean {
        if (!this.requiredLanguageIds || this.requiredLanguageIds.length == 0) {
            return false;
        }

        if (this.requiredLanguageIds.includes(textTypeEntry.languageId)) {
            return true;
        }

        return false;
    }

    entriesChanged(): void {
        this.textTypeEntriesChange.emit(this.textTypeEntries);
    }

    private addTextTypeEntry(languageId: number = null): void {
        this.textTypeEntries.push({ text: null, languageId: languageId });
        this.entriesChanged();
    }
}
